import { useStaticQuery, graphql } from "gatsby";
import Item from "@atoms/Item";
import ItemGroup from "@atoms/ItemGroup";
import Popover from "@atoms/Popover";
import { SUPPORTED_APP_LANGUAGES_DATA, UserLang } from "@constants/common";
import { useRenderLanguage } from "@hooks";
import "./LanguageSwitcher.scss";
import React, { useMemo } from "react";
import LanguageSwitcherController from "./LanguageSwitcherController";
import { useI18next } from "gatsby-plugin-react-i18next";
import { getOriginalLocale } from "@helpers/GlobalHelpers";
import Link from "@components/Link";

interface Props {
	theme?: "dark" | "white";
	variant?: "short" | "full";
	horizontal?: "left" | "right";
	size?: "xxs" | "xs";
	id: string;
}

const LanguageSwitcher: React.FC<Props> = ({
	theme = "dark",
	variant = "short",
	horizontal = "left",
	size,
	id,
}) => {
	const graphqlData = useStaticQuery(graphql`
        query {
            aepsyCms {
                storiesDE: stories(pagination: { limit: 100 }, locale: "de-CH") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				storiesEN: stories(pagination: { limit: 100 }, locale: "en") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				storiesFR: stories(pagination: { limit: 100 }, locale: "fr-CH") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				storiesIT: stories(pagination: { limit: 100 }, locale: "it") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
					}
                }
                websiteUrlsDE: websiteUrls(
                    locale: "de-CH"
                    pagination: { limit: 999 }
                    filters: { type: { eq: "singlePage" } }
                ) {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				websiteUrlsEN: websiteUrls(pagination: { limit: 999 }, locale: "en", filters: { type: { eq: "singlePage" } }) {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				websiteUrlsFR: websiteUrls(pagination: { limit: 999 }, locale: "fr-CH", filters: { type: { eq: "singlePage" } }) {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				websiteUrlsIT: websiteUrls(pagination: { limit: 999 }, locale: "it", filters: { type: { eq: "singlePage" } }) {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
                chaptersDE: chapters(pagination: { limit: 100 }, locale: "de-CH") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				chaptersEN: chapters(pagination: { limit: 100 }, locale: "en") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				chaptersFR: chapters(pagination: { limit: 100 }, locale: "fr-CH") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				chaptersIT: chapters(pagination: { limit: 100 }, locale: "it") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
					}
				}
                locationsDE: locations(pagination: { limit: 100 }, locale: "de-CH") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				locationsEN: locations(pagination: { limit: 100 }, locale: "en") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				locationsFR: locations(pagination: { limit: 100 }, locale: "fr-CH") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
				locationsIT: locations(pagination: { limit: 100 }, locale: "it") {
                    slug
                    locale
                    localizations {
                        slug
                        locale
                    }
                }
            }
        }
    `);

	const getStoriesSlugs = (aepsyCms: any) => {
		const { storiesDE, storiesEN, storiesFR, storiesIT } = aepsyCms;
		return [...storiesDE, ...storiesEN, ...storiesFR, ...storiesIT];
	};

	const getPageConfigurationsSlugs = (aepsyCms: any) => {
		const { websiteUrlsDE, websiteUrlsEN, websiteUrlsFR, websiteUrlsIT } =
			aepsyCms;
		return [
			...websiteUrlsDE,
			...websiteUrlsEN,
			...websiteUrlsFR,
			...websiteUrlsIT,
		];
	};

	const getChapterPagesSlugs = (aepsyCms: any) => {
		const { chaptersDE, chaptersEN, chaptersFR, chaptersIT } = aepsyCms;
		return [...chaptersDE, ...chaptersEN, ...chaptersFR, ...chaptersIT];
	};

	const getLocationPagesSlugs = (aepsyCms: any) => {
		const { locationsDE, locationsEN, locationsFR, locationsIT } = aepsyCms;
		return [...locationsDE, ...locationsEN, ...locationsFR, ...locationsIT];
	};

	const allSlugs = useMemo(() => {
		if (!graphqlData?.aepsyCms) return [];
		const { aepsyCms } = graphqlData;
		const allStorySlugs = getStoriesSlugs(aepsyCms);

		const pageConfigurations = getPageConfigurationsSlugs(aepsyCms);

		const chapterPages = getChapterPagesSlugs(aepsyCms);
		const locationPages = getLocationPagesSlugs(aepsyCms);
		return [
			...allStorySlugs,
			...pageConfigurations,
			...chapterPages,
			...locationPages,
		];
	}, [graphqlData]);

	const { originalPath } = useI18next();
	const { getTranslationObjectByUserLang } = useRenderLanguage();

	const getPathBylang = (lang: any) => {
		const paths = originalPath
			.split("/")
			.filter((item: string) => item.length > 0);

		const originalSubPath = paths[0];

		const foundPage = allSlugs.find(
			(item: any) => item.slug === paths[paths.length - 1],
		);

		const hasSubPath = paths.length >= 2;

		if (foundPage) {
			if (getOriginalLocale(foundPage.locale) === lang) {
				return hasSubPath
					? `/${originalSubPath}/${foundPage.slug}`
					: `/${foundPage.slug}`;
			}

			const localizations = foundPage.localizations;
			const foundByLang = localizations.find(
				(item: { locale: string }) => getOriginalLocale(item.locale) === lang,
			);
			return foundByLang
				? hasSubPath
					? `/${originalSubPath}/${foundByLang.slug}`
					: `/${foundByLang.slug}`
				: null;
		}

		// TODO: remove this condition after ressourcen having multi contents
		if (originalPath.includes("ressourcen") && lang !== UserLang.German) {
			return null;
		}

		return originalPath;
	};

	const langList = useMemo(() => {
		return SUPPORTED_APP_LANGUAGES_DATA.map((item: any) => {
			const { text, flag } = getTranslationObjectByUserLang(item.i18n);
			const to = getPathBylang(item.i18n);
			return {
				language: item.i18n,
				value: item.value,
				text,
				flag,
				to,
				enable: Boolean(to),
			};
		});
	}, [originalPath]);

	return (
		<Popover
			id={id}
			className="Popover--noPadding LanguageSwitcher global_theme-green"
			controller={LanguageSwitcherController}
			controllerProps={{ theme, variant, size }}
			horizontal={horizontal}
			offsetTop={size === "xxs" ? 40 : 50}
			content={
				<ItemGroup border="xl">
					{langList.length ? (
						langList.map((item: any) => {
							if (item?.enable) {
								return (
									<Link
										to={item?.to}
										language={item.language}
										key={item?.value}
									>
										<Item button title={item.text} endSlot={item?.flag} />
									</Link>
								);
							}
							return (
								<Item
									button
									title={item?.text}
									endSlot={item?.flag}
									disabled
									key={item?.value}
								/>
							);
						})
					) : (
						<></>
					)}
				</ItemGroup>
			}
		/>
	);
};

export default LanguageSwitcher;
